import { Radio } from '../form/radio';
import { CheckBox } from '../form/checkbox';
import { cloneDeep, chunk } from 'lodash';
import questionnaireService from './../../services/QuestionnaireService';
import { NumberInput } from '../form/number-input';
import { Type1 } from './eq/type-1';
import { Type2 } from './eq/type-2';
import { Type3 } from './eq/type-3';
import { Flow1 } from './flow-1';
import styled, { css, CSSProp } from 'styled-components';
import { FormValue, Question } from '@app/types';

interface StyleProps {
  isFirst?: boolean;
  isError?: boolean;
  titleStyle?: CSSProp;
}

interface Group {
  key: string;
  type: string;
  title?: string;
  options: Question[];
  layout?: {
    columns?: number;
    bottom?: number;
    top?: number;
    marginBottom?: number;
    width?: string;
    type?: string;
    count?: number;
    title?: string;
  };
  style?: React.CSSProperties;
  appends?: Question[];
  info?: boolean;
  titleStyle?: CSSProp;
}

interface GroupRendererProps {
  group: Group;
  values: Record<string, FormValue>;
  setFieldValue: (field: string, value: FormValue) => void;
  errors: Record<string, boolean>;
}

const OptionContainer = styled.div<StyleProps>`
  border-top: 1px #000 solid;

  ${(props) =>
    props.isFirst &&
    css`
      border-top: none;
    `}
`;

const LayoutContainer = styled.div<StyleProps>`
  background-color: #fff;
  border: 1px #000 solid;
  margin-bottom: 20px;
  border-radius: 6px;

  ${(props) =>
    props.isError &&
    css`
      border-color: #e41e13;
    `}
`;

const GroupTitle = styled.div<StyleProps>`
  text-transform: uppercase;
  font-weight: bold;
  color: #000;
  margin-bottom: 10px;
  font-size: 12px;

  ${(props) =>
    props.isError &&
    css`
      border-color: #e41e13;
    `}

  ${(props) =>
    props.titleStyle &&
    css`
      ${props.titleStyle}
    `}
`;

export const GroupRenderer = ({ group, values, setFieldValue, errors }: GroupRendererProps) => {
  const toggleCheckbox = (checkboxValues: string[], value: string) => {
    if (checkboxValues?.indexOf(value) > -1) {
      return (
        checkboxValues && checkboxValues.filter && checkboxValues.filter((val) => val !== value)
      );
    }

    return [...(checkboxValues || []), value];
  };

  const renderItem = (question: Question) => {
    if ((question?.type || group?.type) === 'radio') {
      return (
        <Radio
          inputKey={question.key}
          label={question.label}
          onChange={(value) => setFieldValue(group.key, value)}
          disabled={
            ((question?.disabled as string)?.length ?? 0) > 0
              ? questionnaireService.evaluateQuestionWithToggle(
                  question.disabled as string | boolean,
                  values,
                  group.key,
                  question.key,
                  setFieldValue
                )
              : undefined
          }
        />
      );
    } else if ((question?.type || group?.type) === 'checkbox') {
      return (
        <CheckBox
          title={question.label ?? ''}
          name={question.key}
          onChange={() =>
            setFieldValue(group.key, toggleCheckbox(values[group.key] as string[], question.key))
          }
        />
      );
    } else if ((question?.type || group?.type) === 'number') {
      return (
        <NumberInput
          placeholder={question.label}
          value={(values[question.questionKey || group.key] as string) || undefined}
          onChange={(value) => setFieldValue(question.questionKey || group.key, value)}
          style={
            !question?.append
              ? { borderWidth: 0, borderColor: 'transparent', width: '99%' }
              : undefined
          }
        />
      );
    } else if ((question?.type || group?.type) === 'hidden') {
      return null;
    }

    console.log('Unknown type', question.type || group.type);
    return null;
  };

  const renderLayout = (options: Question[]) => {
    return (
      <LayoutContainer isError={errors[group.key]}>
        {options.map((question, index) => {
          return (
            <OptionContainer key={`${group.key}_${index}`} isFirst={index === 0}>
              {renderItem(question)}
            </OptionContainer>
          );
        })}
      </LayoutContainer>
    );
  };

  const evaluateAppends = (appends: Question[]) => {
    if (appends?.length > 0) {
      const validAppends = questionnaireService.prepareQuestions(2, appends, values);

      if (validAppends?.length) {
        return validAppends.map((append) => renderAppend(append.option as unknown as Question));
      }
    }

    return null;
  };

  const renderAppend = (question: Question) => {
    return <div key={question.questionKey}>{renderItem({ ...question, append: true })}</div>;
  };

  const renderInfoButton = () => {
    return (
      <button onClick={() => {}}>
        <img
          src={require('./../../assets/icons/info_question_icon.png')}
          className="infoQuestionIcon"
          alt="More Info"
        />
      </button>
    );
  };

  const evaluateLayout = () => {
    const groupOptionsClone = questionnaireService.prepareQuestions(
      group.key,
      cloneDeep(group.options),
      values
    );
    if (group?.layout?.columns) {
      let bottomOptions: Question[] = [];
      let topOptions: Question[] = [];
      if (group?.layout?.bottom) {
        for (let bottomIndex = 0; bottomIndex < group?.layout?.bottom; bottomIndex++) {
          bottomOptions.push(groupOptionsClone.pop() as Question);
        }
      }

      if (group?.layout?.top) {
        for (let topIndex = 0; topIndex < group?.layout?.top; topIndex++) {
          topOptions.push(groupOptionsClone.shift() as Question);
        }
      }

      const optionChunk = chunk(
        groupOptionsClone,
        Math.ceil(groupOptionsClone.length / group.layout.columns)
      );

      return (
        <>
          {topOptions?.length > 0 && <div>{renderLayout(topOptions)}</div>}
          <div
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              marginBottom: group.layout.marginBottom || undefined
            }}
          >
            {optionChunk.map((chunk, index) => {
              return (
                <div
                  key={`${group.key}_chunk_${index}`}
                  style={{
                    width: `${100 / optionChunk.length - 0.25 * optionChunk.length}%`
                  }}
                >
                  {renderLayout(chunk)}
                  {optionChunk.length - 1 === index &&
                    bottomOptions?.length < 1 &&
                    evaluateAppends(group?.appends as Question[])}
                </div>
              );
            })}
          </div>
          {bottomOptions?.length > 0 && (
            <div>
              {renderLayout(bottomOptions.reverse())}
              {evaluateAppends(group?.appends as Question[])}
            </div>
          )}
        </>
      );
    }

    if (group?.layout?.type === 'split') {
      const grouped = groupOptionsClone.splice(0, group?.layout?.count);
      return (
        <div>
          {renderLayout(grouped)}
          <p>{group?.layout?.title}</p>
          {renderLayout(groupOptionsClone)}
          {evaluateAppends(group.appends as Question[])}
        </div>
      );
    }

    return (
      <div>
        {renderLayout(groupOptionsClone)}
        {evaluateAppends(group.appends as Question[])}
      </div>
    );
  };

  const renderSeparator = (g: Group) => {
    return (
      <div>
        <p>{g.options[0].title}</p>
      </div>
    );
  };

  const renderEQ = (g: Group) => {
    switch (g.type) {
      case 'eq_type_1':
        return <Type1 g={g} group={group} values={values} setFieldValue={setFieldValue} />;
      case 'eq_type_2':
        return <Type2 g={g} group={group} values={values} setFieldValue={setFieldValue} />;
      case 'eq_type_3':
        return <Type3 g={g} group={group} values={values} setFieldValue={setFieldValue} />;
      default:
        console.log('unknown type', g.type);
        return null;
    }
  };

  const renderRadioGroup = (g: Group) => {
    return (
      <div>
        <div>
          {g.options.map((option) => (
            <div key={`label_${option.key}`}>
              <p key={option.key}>{option.title}</p>
            </div>
          ))}
        </div>
        <div>
          <div>
            <div style={{ flexDirection: 'row' }}>
              {g.options[0].options?.map((answer) => (
                <div key={`label_${g.key}_${g.key}_${answer.key}`}>
                  <p>{answer.label}</p>
                </div>
              ))}
            </div>
          </div>
          {g.options.map((question, index) => {
            return (
              <div key={g.key + '_' + g.key + '_' + index.toString()}>
                <div style={{ flexDirection: 'row' }}>
                  {g.options[0].options?.map((answer) => (
                    <div key={question.key + '_' + answer.key}>
                      <Radio
                        inputKey={answer.key}
                        onChange={(value) => setFieldValue(question.key, value)}
                        style={{ paddingRight: 8, justifyContent: 'center' }}
                        radioStyle={{
                          marginRight: 0,
                          ...(errors[question.key] ? { borderColor: 'red' } : undefined)
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (group.type === 'grouped') {
    return renderRadioGroup(group);
  } else if (group.type.startsWith('eq_type_')) {
    return renderEQ(group);
  } else if (group.type.startsWith('flow_1')) {
    return <Flow1 group={group} values={values} setFieldValue={setFieldValue} errors={errors} />;
  } else if (group.type === 'desc_separator') {
    return renderSeparator(group);
  } else {
    return (
      <div
        style={{
          width: group?.layout?.width || '24%'
        }}
      >
        <div style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
          <GroupTitle isError={errors[group.key]} titleStyle={group?.titleStyle}>
            {group.title}
          </GroupTitle>
          {group.info && Boolean(group.info) === true && renderInfoButton()}
        </div>
        {evaluateLayout()}
      </div>
    );
  }
};
